import { useRuntimeConfig, useState } from '#imports'
import { onMounted } from 'vue'

import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'

import { type Offer, isOffer } from '~/api/tradein-apac/types'

type PersistedOffer = {
  offer: Offer
  timestamp: number
}

export function useTradeInAuOffer() {
  const {
    public: { TRADEIN_AU_OFFER_TTL_MS },
  } = useRuntimeConfig()
  const localStorage = useLocalStorage()
  const marketKey = useMarketplace().market.countryCode.toLowerCase()
  const STORAGE_KEY = `tradein_${marketKey}_accepted_offer`
  const DEFAULT_TTL_MS = 3 * 24 * 60 * 60 * 1000
  const offerTtlMs = Number(TRADEIN_AU_OFFER_TTL_MS) || DEFAULT_TTL_MS

  const acceptedOffer = useState<Offer | undefined>(
    `tradein-${marketKey}:accepted-offer`,
    () => undefined,
  )

  function setAcceptedOffer(offer: Offer) {
    if (!isOffer(offer)) return
    acceptedOffer.value = offer
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ offer, timestamp: Date.now() }),
    )
  }

  onMounted(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return

    try {
      const { offer, timestamp } = JSON.parse(stored) as PersistedOffer
      const elapsed = Date.now() - timestamp

      if (elapsed > offerTtlMs) {
        localStorage.removeItem(STORAGE_KEY)

        return
      }

      if (!isOffer(offer)) {
        localStorage.removeItem(STORAGE_KEY)

        return
      }

      acceptedOffer.value = offer
    } catch {
      // corrupted data in store - not supposed to happen
      localStorage.removeItem(STORAGE_KEY)
    }
  })

  return {
    acceptedOffer,
    setAcceptedOffer,
  }
}
